import { ArrowLeft, FileSignature, Pencil, Receipt, Send } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { accedeModule } from '@/auth/modules';
import { toast } from 'sonner';
import { controlesDe } from '@/api/client';
import { useActionsFed, useActionsTdr, useNotes, useTdr } from '@/api/hooks';
import type { DecisionApi } from '@/api/types';
import { useSession } from '@/auth/session';
import { BoutonPdf, Chargement, CircuitTimeline, ConfirmationDialog, ControlesListe, DecisionDialog, ErreurBloc, EtapeBadge, Historique, PageHeader, PaiementBadge, StatutBadge } from '@/components/common';
import { PiecesJointes } from '@/components/pieces-jointes';
import { Badge } from '@/components/ui/badge';
import { BandeauCorrection } from '@/components/documents';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { dateFr, dateHeureFr, montant } from '@/lib/utils';

const NATURES: Record<string, string> = { PERDIEM: 'Perdiem', LOGEMENT: 'Logement', HEBERGEMENT: 'Hébergement', TRANSPORT: 'Transport', PROTOCOLE: 'Protocole', REPRESENTATION: 'Représentation', FRAIS_TERMINAUX: 'Frais terminaux' };
const TYPE_MISSION: Record<string, string> = { INTER: 'interprovinciale', INTRA: 'intraprovinciale', EXT: "à l'étranger" };

function Liste({ titre, items }: { titre: string; items: string[] }) {
  if (!items.length) return null;
  return (
    <div>
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{titre}</div>
      <ul className="list-disc pl-5 text-sm">
        {items.map((x, i) => (
          <li key={i}>{x}</li>
        ))}
      </ul>
    </div>
  );
}

export function TdrDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const session = useSession();
  const u = session!.utilisateur;
  const { data, isLoading, error } = useTdr(id);
  const notes = useNotes({ tdrId: id });
  const actions = useActionsTdr();
  const fed = useActionsFed();
  const [dialogue, setDialogue] = useState<'decision' | 'soumettre' | 'resoumettre' | 'fed' | null>(null);

  if (isLoading) return <Chargement />;
  if (error || !data) return <ErreurBloc erreur={error} />;
  const { tdr, calcul, circuit, actions: a } = data;
  const enRedaction = tdr.statut === 'BROUILLON' || tdr.statut === 'A_CORRIGER';
  const couvert = data.feds.filter((f) => f.statut !== 'REJETE' && f.statut !== 'ANNULE').reduce((s, f) => s + f.montantUsd, 0);
  const peutCreerFed = tdr.statut === 'AUTORISE' && tdr.emetteurId === u.agentId && data.montantTotal - couvert > 0.005;
  const peutEmettre = tdr.emetteurId === u.agentId || u.habilitations.some((h) => (h.profil === 'EMETTEUR' || h.profil === 'ASSISTANT' || h.profil === 'DIRECTEUR') && (h.structureId === null || h.structureId === tdr.structureId));
  const peutNote = (tdr.statut === 'SOUMIS' || tdr.statut === 'AUTORISE') && peutEmettre;
  const blocA = enRedaction ? (calcul?.total ?? 0) : tdr.montantA;
  const blocB = enRedaction ? (tdr.autresFrais ?? []).reduce((s, x) => s + x.montant, 0) : tdr.montantB;
  const erreur = (e: unknown) => {
    const c = controlesDe(e);
    toast.error(e instanceof Error ? e.message : 'Action impossible.', { description: c.length ? c.map((x) => x.message).join(' · ') : undefined });
  };

  const decider = async (decision: DecisionApi, motif?: string) => {
    try {
      await actions.decider.mutateAsync({ id: tdr.id, decision, motif });
      toast.success('Décision enregistrée.');
      setDialogue(null);
    } catch (e) {
      erreur(e);
    }
  };
  const soumettre = async () => {
    try {
      const r = await actions.soumettre.mutateAsync(tdr.id);
      toast.success(`TDR ${r.tdr.numero} soumis.`);
      setDialogue(null);
    } catch (e) {
      erreur(e);
    }
  };
  const resoumettre = async (reponse: string) => {
    try {
      await actions.resoumettre.mutateAsync({ id: tdr.id, reponse: reponse || undefined });
      toast.success('TDR soumis à nouveau : retour direct au validateur qui a demandé la correction.');
      setDialogue(null);
    } catch (e) {
      erreur(e);
    }
  };
  const creerFed = async (modePaiement: string) => {
    try {
      const r = await fed.depuisTdr.mutateAsync({ tdrId: tdr.id, modePaiement: modePaiement || undefined });
      toast.success('FED créé en brouillon.');
      setDialogue(null);
      navigate(`/fed/${r.fed.id}`);
    } catch (e) {
      erreur(e);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        retour={
          <Link to="/tdr" className="mb-1 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="size-4" /> Termes de référence
          </Link>
        }
        titre={
          <span className="flex flex-wrap items-center gap-2">
            {tdr.numero ?? 'Brouillon'} <StatutBadge statut={tdr.statut} /> {tdr.statut === 'SOUMIS' && <EtapeBadge etape={data.etapeCourante} />}
            {tdr.urgence && <Badge variant="destructive">Urgent</Badge>}
            {tdr.memorial && <Badge variant="outline">Mémorial</Badge>}
          </span>
        }
        sousTitre={
          <>
            {tdr.titre} · {tdr.structure?.libelle} · émis par {tdr.emetteur?.prenom} {tdr.emetteur?.nom}
            {tdr.dateSoumission ? ` · soumis le ${dateHeureFr(tdr.dateSoumission)}` : ''}
            {tdr.dateAutorisation ? ` · autorisé le ${dateHeureFr(tdr.dateAutorisation)}` : ''}
          </>
        }
        actions={
          <>
            <BoutonPdf chemin={`/pdf/tdr/${tdr.id}`} nom={`${tdr.numero ?? 'TDR'}.pdf`} />
            {a.modifier && (
              <Button variant="outline" asChild>
                <Link to={`/tdr/${tdr.id}/modifier`}>
                  <Pencil className="size-4" /> Modifier
                </Link>
              </Button>
            )}
            {a.soumettre && (
              <Button onClick={() => setDialogue('soumettre')}>
                <Send className="size-4" /> Soumettre
              </Button>
            )}
            {a.resoumettre && (
              <Button onClick={() => setDialogue('resoumettre')}>
                <Send className="size-4" /> Soumettre à nouveau
              </Button>
            )}
            {a.decider && <Button onClick={() => setDialogue('decision')}>Décider</Button>}
            {peutNote && (
              <Button variant="outline" asChild>
                <Link to={`/tdr/${tdr.id}/note/nouvelle`}>
                  <FileSignature className="size-4" /> Note explicative
                </Link>
              </Button>
            )}
            {peutCreerFed && (
              <Button variant="secondary" onClick={() => setDialogue('fed')}>
                <Receipt className="size-4" /> {couvert > 0 ? 'Créer le FED complémentaire' : 'Créer le FED'}
              </Button>
            )}
          </>
        }
      />
      <BandeauCorrection statut={tdr.statut} correction={data.correction} motif={tdr.motifAnnulation} automatique={tdr.annulationAutomatique} />

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-lg border bg-card p-4">
              <div className="text-xs uppercase text-muted-foreground">Bloc A · frais de mission</div>
              <div className="num text-xl font-semibold">{montant(blocA, true)}</div>
              {calcul && <div className="text-xs text-muted-foreground">barème {calcul.baremeVersion}{tdr.revisions?.length ? ' · révisé par note explicative' : ''}</div>}
            </div>
            <div className="rounded-lg border bg-card p-4">
              <div className="text-xs uppercase text-muted-foreground">Bloc B · autres frais</div>
              <div className="num text-xl font-semibold">{montant(blocB, true)}</div>
            </div>
            <div className="rounded-lg border bg-primary/5 p-4">
              <div className="text-xs uppercase text-muted-foreground">Total</div>
              <div className="num text-xl font-semibold">{montant(data.montantTotal, true)}</div>
              <div className="text-xs text-muted-foreground">
                {tdr.ligneBudgetaire?.code} · {tdr.ligneBudgetaire?.libelle}
              </div>
              {tdr.ligneBudgetaire?.id && accedeModule(u.profils, 'plan') && (
                <Link to={`/plan/${tdr.ligneBudgetaire.id}`} className="mt-1 block text-xs text-primary hover:underline">
                  Fiche de suivi de la sous-activité
                </Link>
              )}
            </div>
          </div>

          {data.controles.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Contrôles avant soumission</CardTitle>
              </CardHeader>
              <CardContent>
                <ControlesListe controles={data.controles} />
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Contenu</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              {tdr.contenu.contexte && (
                <div>
                  <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Contexte</div>
                  <p className="whitespace-pre-line">{tdr.contenu.contexte}</p>
                </div>
              )}
              <div>
                <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Objectif global</div>
                <p>{tdr.contenu.objectifGlobal}</p>
              </div>
              <Liste titre="Objectifs spécifiques" items={tdr.contenu.objectifsSpecifiques} />
              <Liste titre="Résultats attendus" items={tdr.contenu.resultats} />
              {tdr.contenu.methodologie && (
                <div>
                  <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Méthodologie</div>
                  <p className="whitespace-pre-line">{tdr.contenu.methodologie}</p>
                </div>
              )}
              <Liste titre="Livrables" items={tdr.contenu.livrables} />
              {tdr.contenu.cible && (
                <div>
                  <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Cible</div>
                  <p>{tdr.contenu.cible}</p>
                </div>
              )}
              {tdr.urgence && tdr.justificationUrgence && (
                <div>
                  <div className="text-xs font-medium uppercase tracking-wide text-red-700">Justification de l'urgence</div>
                  <p>{tdr.justificationUrgence}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {tdr.equipes.map((e, i) => (
            <Card key={e.id}>
              <CardHeader>
                <CardTitle>
                  {e.nom} <span className="font-normal text-muted-foreground">· mission {TYPE_MISSION[tdr.zone === 'EXT' ? 'EXT' : e.typeMission]}</span>
                  {calcul && <span className="num float-right">{montant(calcul.parEquipe[i] ?? 0, true)}</span>}
                </CardTitle>
                <CardDescription>
                  Du {dateFr(e.debut)} au {dateFr(e.fin)} · départ {e.provinceDepart} · {e.destinations.map((d) => `${d.localite} (${d.province})`).join(', ')} · {e.transports.join(', ') || 'transport non précisé'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Agent</TableHead>
                      <TableHead>Nature</TableHead>
                      <TableHead className="text-right">Montant</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {e.membres.map((m) => {
                      const lignes = calcul?.lignes.filter((l) => l.equipe === i && l.agentId === m.agentId) ?? [];
                      return lignes.length ? (
                        lignes.map((l, k) => (
                          <TableRow key={`${m.id}-${k}`}>
                            <TableCell className="whitespace-nowrap align-top">
                              {k === 0 && (
                                <>
                                  {m.agent.prenom} {m.agent.nom}
                                  {m.chef && <span className="ml-1 text-xs text-muted-foreground">(chef)</span>}
                                  <div className="text-xs text-muted-foreground">{m.agent.categorie} · {m.agent.base === 'COORDINATION' ? m.agent.province : 'siège'}</div>
                                </>
                              )}
                            </TableCell>
                            <TableCell>
                              <div>{NATURES[l.nature] ?? l.nature}</div>
                              <div className="text-xs text-muted-foreground">{l.description}</div>
                            </TableCell>
                            <TableCell className="num text-right">{montant(l.montant, true)}</TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow key={m.id}>
                          <TableCell>
                            {m.agent.prenom} {m.agent.nom}
                          </TableCell>
                          <TableCell className="text-muted-foreground">calcul indisponible</TableCell>
                          <TableCell />
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          ))}

          {(tdr.autresFrais?.length ?? 0) > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Bloc B · autres frais</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableBody>
                    {tdr.autresFrais!.map((f, i) => (
                      <TableRow key={i}>
                        <TableCell>{f.description}</TableCell>
                        <TableCell className="num text-right">{montant(f.montant, true)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
          <PiecesJointes objetType="TDR" objetId={tdr.id} typesSuggeres={['LISTE_PRESENCE', 'RAPPORT', 'COURRIER', 'AUTRE']} description="Facultatives ; après soumission, ajout réservé au contrôle de gestion, aux DGA et au DG." />
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Circuit de validation</CardTitle>
              {circuit && <CardDescription>{circuit.context.amount.toLocaleString('fr-FR')} USD · {circuit.steps.length} étapes</CardDescription>}
            </CardHeader>
            <CardContent>
              <CircuitTimeline circuit={circuit} />
            </CardContent>
          </Card>
          {data.feds.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Engagements liés</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                {data.feds.map((f) => (
                  <div key={f.id} className="flex flex-wrap items-center justify-between gap-2">
                    <Link to={`/fed/${f.id}`} className="font-medium text-primary hover:underline">
                      {f.numero ?? 'FED (brouillon)'}
                    </Link>
                    <span className="flex items-center gap-1">
                      <StatutBadge statut={f.statut} />
                      {f.statut === 'AUTORISE' && <PaiementBadge etat={f.etatPaiement} />}
                    </span>
                    <span className="num">{montant(f.montantUsd, true)}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
          {(notes.data?.length ?? 0) > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Notes explicatives</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                {notes.data!.map((n) => (
                  <div key={n.id} className="flex flex-wrap items-center justify-between gap-2">
                    <Link to={`/ne/${n.id}`} className="min-w-0 flex-1 truncate font-medium text-primary hover:underline">
                      {n.numero ?? 'Note (brouillon)'} · {n.objet}
                    </Link>
                    <StatutBadge statut={n.statut} />
                    {n.impactBudgetaire && (
                      <span className={`num ${n.ecart > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>
                        {n.ecart >= 0 ? '+' : ''}
                        {montant(n.ecart, true)}
                      </span>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
          {(tdr.revisions?.length ?? 0) > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Révisions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-1 text-sm">
                {tdr.revisions!.map((r, i) => (
                  <div key={i} className="flex justify-between gap-2">
                    <span>
                      {r.note} · {dateFr(r.date)}
                    </span>
                    <span className="num">
                      {montant(r.montantAncien, true)} → {montant(r.montantNouveau, true)}
                    </span>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
          {circuit && circuit.history.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Historique</CardTitle>
              </CardHeader>
              <CardContent>
                <Historique circuit={circuit} />
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <DecisionDialog ouvert={dialogue === 'decision'} onOpenChange={(o) => setDialogue(o ? 'decision' : null)} decisions={a.decisions} etape={a.etape} onConfirmer={decider} chargement={actions.decider.isPending} />
      <ConfirmationDialog
        ouvert={dialogue === 'soumettre'}
        onOpenChange={(o) => setDialogue(o ? 'soumettre' : null)}
        titre="Soumettre le TDR"
        description="Un numéro sera attribué, le calcul figé et le circuit de validation ouvert. Le TDR passe en lecture seule."
        libelle="Soumettre"
        onConfirmer={soumettre}
        chargement={actions.soumettre.isPending}
      />
      <ConfirmationDialog
        ouvert={dialogue === 'resoumettre'}
        onOpenChange={(o) => setDialogue(o ? 'resoumettre' : null)}
        titre="Soumettre à nouveau"
        description={`Le dossier retourne directement à l'étape ${circuit?.returnedBy ?? ''} ; les validations déjà acquises sont conservées.`}
        libelle="Soumettre à nouveau"
        onConfirmer={resoumettre}
        chargement={actions.resoumettre.isPending}
        champ={{ label: 'Réponse au validateur', placeholder: 'Ce qui a été corrigé.' }}
      />
      <ConfirmationDialog
        ouvert={dialogue === 'fed'}
        onOpenChange={(o) => setDialogue(o ? 'fed' : null)}
        titre="Créer le FED à partir de ce TDR"
        description="Une ligne par agent d'après le calcul, les autres frais en lignes fournisseur. Le FED est créé en brouillon."
        libelle="Créer le FED"
        onConfirmer={creerFed}
        chargement={fed.depuisTdr.isPending}
        champ={{ label: 'Mode de paiement (facultatif)', placeholder: 'Espèces, virement…', lignes: 1 }}
      />
    </div>
  );
}
